/** The stage's six views, named by what each shows, never by position, so a
    view can be added or reordered without renaming the others. One pairing is
    positional: the room opens out of the Decisions list (the drill in
    `Stage.tsx`) only when its step directly follows the list's. */
export const STAGE_VIEW = {
  PLAN: "plan",
  GRAPH: "graph",
  DECISIONS: "decisions",
  ROOM: "room",
  SIGNAL: "signal",
  REVIEW: "review",
} as const;
export type StageView = (typeof STAGE_VIEW)[keyof typeof STAGE_VIEW];

/** Where the stage fits beside its captions below the 60px sticky header: a
    600px panel needs the height, and the two columns need the width. Below
    either, each view sits under its own caption. */
export const STAGE_PIN_QUERY = "(min-width: 1024px) and (min-height: 700px)";

/** The caption crossing the viewport's middle picks the pinned view. */
export const ACTIVE_STEP_ROOT_MARGIN = "-50% 0px -50% 0px";

/**
 * How far a view's one-time play has got, set on its frame as
 * `data-stage-play`. A view is idle until it first shows, plays its timeline
 * once, and is done for good; its CSS module hides the before-state under
 * `[data-motion]` while idle or playing, and runs the timeline while playing.
 * Done renders the final state with no animation, so a view remounted by a
 * layout flip neither replays nor strands halfway.
 */
export const PLAY_PHASE = {
  IDLE: "idle",
  PLAYING: "playing",
  DONE: "done",
} as const;
export type PlayPhase = (typeof PLAY_PHASE)[keyof typeof PLAY_PHASE];

/** How a view came on: set on its frame as `data-stage-entry` for the play
    that followed. The room entered by drilling in waits for the reveal. */
export const PLAY_ENTRY = {
  DRILL: "drill",
} as const;
export type PlayEntry = (typeof PLAY_ENTRY)[keyof typeof PLAY_ENTRY];

/** Stacked, a view plays once this much of it is in view. */
export const STACKED_PLAY_THRESHOLD = 0.45;

/** Stacked, Make the call leaves the room's caption this far under the header. */
export const STACKED_SCROLL_CLEARANCE = 16;

/** Marks on the two nodes the drill-in measures, the open decision's row in
    the Decisions list and the room's card, so the stage finds them inside its
    own frames. */
export const DRILL_FROM_ATTRIBUTE = "data-drill-from";
export const DRILL_TO_ATTRIBUTE = "data-drill-to";
